import { defineMessages } from 'react-intl'
import { featureNames } from 'lib/localization'
import { loadExistingMessages } from 'features/search/LoadExisting'
import { messages as selectionMessages } from 'features/recumbent/PlannerSidebar/SelectionTools'

export const names = defineMessages({
  advancedRouteEditing: {
    id: 'UpgradeModal.advancedRouteEditingName',
    defaultMessage: 'Advanced Route Planning'
  },
  pdfMapsAndCuesheets: {
    id: 'UpgradeModal.pdfMapsAndCuesheetsName',
    defaultMessage: 'PDF Maps & Cuesheets'
  },
  rideCleanup: {
    id: 'UpgradeModal.rideCleanupName',
    defaultMessage: 'Ride Cleanup'
  },
  rideCenter: {
    id: 'UpgradeModal.rideCenterName',
    defaultMessage: 'Ride Center'
  },
  privateSegments: {
    id: 'UpgradeModal.privateSegmentsName',
    defaultMessage: 'Private Segments'
  },
  mobileNavigation: {
    id: 'UpgradeModal.mobileNavigationName',
    defaultMessage: 'Mobile Navigation'
  },
  offlineMaps: {
    id: 'UpgradeModal.offlineMapsName',
    defaultMessage: 'Offline Maps'
  },
  liveLogging: {
    id: 'UpgradeModal.liveLoggingName',
    defaultMessage: 'Live Logging'
  },
  maintenanceLog: {
    id: 'UpgradeModal.maintenanceLogName',
    defaultMessage: 'Maintenance Log'
  },
  deviceExport: {
    id: 'UpgradeModal.deviceExportName',
    defaultMessage: 'Device Export'
  },
  leaderboardFiltering: {
    id: 'UpgradeModal.leaderboardFilteringName',
    defaultMessage: 'Event Leaderboard Filtering'
  },
  stationaryRides: {
    id: 'UpgradeModal.stationaryRidesName',
    defaultMessage: 'Stationary Rides'
  },
  fitExport: {
    id: 'UpgradeModal.fitExportName',
    defaultMessage: 'FIT File Export'
  },
  pinning: {
    id: 'UpgradeModal.pinningName',
    defaultMessage: 'Pinning'
  },
  plannerAddToCuesheet: {
    id: 'UpgradeModal.plannerAddToCuesheetName',
    defaultMessage: 'Custom Cues'
  },
  plannerAddRoute: {
    id: 'UpgradeModal.plannerAddRouteName',
    defaultMessage: 'Add Routes'
  },
  plannerReviewCues: {
    id: 'UpgradeModal.plannerReviewCuesName',
    defaultMessage: 'Review Your Cues'
  },
  plannerDuplicate: {
    id: 'UpgradeModal.plannerDuplicateName',
    defaultMessage: 'Duplicate Your Route'
  },
  plannerTrace: {
    id: 'UpgradeModal.plannerTraceName',
    defaultMessage: 'The Trace Tool'
  },
  plannerSplitRoute: {
    id: 'UpgradeModal.plannerSplitRouteName',
    defaultMessage: 'Split Up Your Route'
  },
  plannerCrop: {
    id: 'UpgradeModal.plannerCropName',
    defaultMessage: 'Crop Tool'
  },
  plannerMobile: {
    id: 'UpgradeModal.plannerMobileName',
    defaultMessage: 'Ride with GPS Mobile App'
  },
  heatmap: {
    id: 'UpgradeModal.heatmap',
    defaultMessage: 'Heatmap'
  }
})

names.estimatedTime = featureNames.estimatedTime
names.plannerAddPoi = featureNames.pointsOfInterest
names.heatmap = featureNames.heatmap
names.plannerImportRoutes = loadExistingMessages.importRoutesAndRides
names.plannerDeleteSelection = selectionMessages.deleteSelection
names.plannerFlattenElevation = selectionMessages.flattenElevation
names.plannerExcludeFromMetrics = selectionMessages.exclFromMetrics
names.plannerRemoveControlPoints = selectionMessages.remCtrlPoints
names.plannerChangeColor = selectionMessages.changeColor

export const descriptions = defineMessages({
  advancedRouteEditing: {
    id: 'UpgradeModal.advancedRouteEditingDescription',
    defaultMessage: 'Plan and edit multi-day routes, add custom cues and points of interest, combine or split routes, and easily turn rides into routes.'
  },
  pdfMapsAndCuesheets: {
    id: 'UpgradeModal.pdfMapsAndCuesheetsDescription',
    defaultMessage: 'Craft the perfect map with multiple size and orientation options, ordered points of interest, custom cues, and multiple map styles to choose from.'
  },
  rideCleanup: {
    id: 'UpgradeModal.rideCleanupDescription',
    defaultMessage: 'Remove bad data, trim ends, exclude sections from metrics, or join multiple rides together. Ride Cleanup Tools always ensure accurate data.'
  },
  rideCenter: {
    id: 'UpgradeModal.rideCenterDescription',
    defaultMessage: 'Graphs and summaries of your data. Sort, filter, tag, and bulk edit your rides. It’s all here in the Ride Center.'
  },
  estimatedTime: {
    id: 'UpgradeModal.estimatedTimeDescription',
    defaultMessage: 'Know how long a ride is going to take with personalized estimated time for every route you create or find.'
  },
  privateSegments: {
    id: 'UpgradeModal.privateSegmentsDescription',
    defaultMessage: 'Keep all your rides private and still track your progress with your own personal leaderboards and private segments.'
  },
  mobileNavigation: {
    id: 'UpgradeModal.mobileNavigationDescription',
    defaultMessage: 'Spoken turn-by-turn directions and visual cues let you keep you head up and enjoy the ride.'
  },
  offlineMaps: {
    id: 'UpgradeModal.offlineMapsDescription',
    defaultMessage: 'No cell connection? No problem. Save battery and data with offline maps. Get our voice navigation and maps anywhere you go, even with your phone in airplane mode.'
  },
  liveLogging: {
    id: 'UpgradeModal.liveLoggingDescription',
    defaultMessage: 'Share your rides in real time with friends and family. Let them track your progress, and even type comments which our mobile app will read aloud to you.'
  },
  maintenanceLog: {
    id: 'UpgradeModal.maintenanceLogDescription',
    defaultMessage: 'Keep your bike in tip-top shape by planning and tracking your maintenance.'
  },
  deviceExport: {
    id: 'UpgradeModal.deviceExportDescription',
    defaultMessage: 'After you have planned your route, export it to your Garmin device for navigation.'
  },
  leaderboardFiltering: {
    id: 'UpgradeModal.leaderboardFilteringDescription',
    defaultMessage: 'The overall leaderboard will show all matched participants and rank them for each segment across the entire event.'
  },
  stationaryRides: {
    id: 'UpgradeModal.stationaryRidesDescription',
    defaultMessage: 'Use the mobile app for iPhone and Android, or your Garmin unit to record your ride to get the moving time, heart rate, speed, candence and/or power data, and we’ll track it with the rest of your rides.'
  },
  fitExport: {
    id: 'UpgradeModal.fitExportDescription',
    defaultMessage: 'Basic and Premium subscribers can download their routes as FIT files. FIT files are smaller, process faster, and provide the best navigation experience with Garmin devices.'
  },
  pinning: {
    id: 'UpgradeModal.pinningDescription',
    defaultMessage: 'There are so many routes, rides, rider profiles, and gear on our site it’s no doubt you’ll stumble upon something you don’t want to forget. We know the feeling, and we’re here to help you remember! Just pin whatever you want to keep track of and you’ll easily be able to locate it later on both the mobile app and website.'
  },
  plannerAddToCuesheet: {
    id: 'UpgradeModal.plannerAddToCuesheet',
    defaultMessage: 'Add custom cues to your route to include more information and alerts about a particularly tricky turn, offer reminders about upcoming climbs, or give specific details about an area.'
  },
  plannerAddPoi: {
    id: 'UpgradeModal.plannerAddPoi',
    defaultMessage: 'Add Points of Interest (POI) to your route to provide extra details on your map. You can select icons, names, add descriptions and even upload photos for your favorite places along the way.'
  },
  plannerImportRoutes: {
    id: 'UpgradeModal.plannerImportRoutes',
    defaultMessage: 'Get the big picture: Import your routes and rides, or other public routes and rides you find, to compare, edit, and customize to make your own.'
  },
  plannerAddRoute: {
    id: 'UpgradeModal.plannerAddRoute',
    defaultMessage: 'You can create many different routes in the route planner at once and view them all at the same time. This is especially useful when planning a multi-day trip, or organizing a ride with several different length options. '
  },
  plannerReviewCues: {
    id: 'UpgradeModal.plannerReviewCues',
    defaultMessage: 'The Cue Review Tool allows you to quickly go through all of your cues before you ride, so that you can make any changes or corrections and ensure sure that your cuesheet is exactly how you want it. '
  },
  plannerDuplicate: {
    id: 'UpgradeModal.plannerDuplicate',
    defaultMessage: 'There\'s no need to draw every route from scratch; you can use the Duplicate Tool to easily create a variation of any of your routes. This is especially useful when planning a group ride with several different route length options.'
  },
  plannerTrace: {
    id: 'UpgradeModal.plannerTrace',
    defaultMessage: 'If you upload a route or ride without a cuesheet or a file from another website, you can use the Trace Tool to re-create it with turn by turn directions.'
  },
  plannerSplitRoute: {
    id: 'UpgradeModal.plannerSplitRoute',
    defaultMessage: 'The Split Route Tool allows you to take a route and split it into as many smaller routes as you would like, without affecting the original route.'
  },
  plannerCrop: {
    id: 'UpgradeModal.plannerCrop',
    defaultMessage: 'Trimming the ends of a route allows you to select the exact part you want, and easily remove the rest. Riding to and from group rides, but only need to navigate the portion with the group? This tool will help with that.'
  },
  plannerDeleteSelection: {
    id: 'UpgradeModal.plannerDeleteSelection',
    defaultMessage: 'Need to remove a part of a route, but want to keep the rest? Highlight and delete specific sections of your route, while keeping the portions you want. '
  },
  plannerFlattenElevation: {
    id: 'UpgradeModal.plannerFlattenElevation',
    defaultMessage: 'Make sure you\'re getting accurate credit for your climbs: The Flatten Elevation Tool helps you correct elevation inaccuracies that can be caused by bridges, tunnels, or poor GPS.'
  },
  plannerExcludeFromMetrics: {
    id: 'UpgradeModal.plannerExcludeFromMetrics',
    defaultMessage: 'Exclude a part of the metrics for your route, but retain your metrics data for the total route. Taking a bus or ferry while on your ride? This will come in handy. '
  },
  plannerRemoveControlPoints: {
    id: 'UpgradeModal.plannerRemoveControlPoints',
    defaultMessage: 'Quickly re-route any section by removing control points and dragging your route.'
  },
  plannerChangeColor: {
    id: 'UpgradeModal.plannerChangeColor',
    defaultMessage: 'Use color to highlight sections of your route and distinguish surface type.'
  },
  plannerMobile: {
    id: 'UpgradeModal.plannerMobal',
    defaultMessage: 'Take the Ride with GPS mobile app for iOS and Android on your nxt ride. Voice Navigation, Offline Maps, mobile route planning on the fly, and even the ability for friends and family to track you in real time. Get inspired, not lost!'
  },
  heatmap: {
    id: 'UpgradeModal.heatmapDesc',
    defaultMessage: 'View the heatmap in the route planner to see where other cyclists are riding, get ideas for places to ride across the world or in your backyard, and then seamlessly create and edit routes in the planner with heat as a guide.'
  }
})

export const featureData = {
  advancedRouteEditing: {
    assetType: 'webm',
    helpPath: '/help/advanced-planning-and-editing',
    premium: true
  },
  pdfMapsAndCuesheets: {
    assetType: 'png',
    helpPath: '/help/create-customize-a-cuesheet'
  },
  rideCleanup: {
    assetType: 'webm',
    helpPath: '/help/ride-clean-up',
    premium: true
  },
  rideCenter: {
    assetType: 'webm',
    helpPath: '/help/ride-center',
    premium: true
  },
  estimatedTime: {
    assetType: 'png',
    helpPath: '/help/estimated-time'
  },
  privateSegments: {
    assetType: 'png',
    helpPath: '/help/private-segments',
    premium: true
  },
  mobileNavigation: {
    assetType: 'png',
    helpPath: '/help/voice-navigation'
  },
  offlineMaps: {
    assetType: 'png',
    helpPath: '/help/offline-maps-iphone'
  },
  liveLogging: {
    assetType: 'png',
    helpPath: '/help/live-logging'
  },
  maintenanceLog: {
    assetType: 'png',
    helpPath: '/help/bicycle-maintenance-log'
  },
  deviceExport: {
    assetType: 'png',
    helpPath: '/help/export-routes-to-garmin-device'
  },
  leaderboardFiltering: {
    assetType: 'png',
    helpPath: '/help/event-leaderboards',
    premium: true
  },
  stationaryRides: {
    assetType: 'png',
    helpPath: '/help/stationary-ride-support',
    premium: true
  },
  fitExport: {
    assetType: 'png',
    helpPath: '/help/export-file-formats'
  },
  pinning: {
    assetType: 'png',
    helpPath: 'https://ridewithgps.com/help/pinning-and-sharing'
  },
  plannerAddToCuesheet: {
    assetType: 'png',
    helpPath: '/help/advanced-planning-and-editing#add-to-cuesheet'
  },
  plannerAddPoi: {
    assetType: 'png',
    helpPath: '/help/advanced-planning-and-editing#add-poi'
  },
  plannerImportRoutes: {
    assetType: 'jpg',
    helpPath: '/help/advanced-planning-and-editing#import-routes-rides'
  },
  plannerAddRoute: {
    assetType: 'png',
    helpPath: '/help/advanced-planning-and-editing#add-route'
  },
  plannerReviewCues: {
    assetType: 'png',
    helpPath: '/help/cue-review-tool'
  },
  plannerDuplicate: {
    assetType: 'png',
    helpPath: '/help/advanced-planning-and-editing#multiple-routes'
  },
  plannerTrace: {
    assetType: 'gif',
    helpPath: '/help/trace-tool'
  },
  plannerSplitRoute: {
    assetType: 'gif',
    helpPath: '/help/split-route-tool'
  },
  plannerCrop: {
    assetType: 'gif',
    helpPath: '/help/ride-clean-up'
  },
  plannerDeleteSelection: {
    assetType: 'gif',
    helpPath: '/help/ride-clean-up'
  },
  plannerFlattenElevation: {
    assetType: 'gif',
    helpPath: '/help/ride-clean-up'
  },
  plannerExcludeFromMetrics: {
    assetType: 'gif',
    helpPath: '/help/ride-clean-up'
  },
  plannerRemoveControlPoints: {
    assetType: 'gif',
    helpPath: '/help/ride-clean-up'
  },
  plannerChangeColor: {
    assetType: 'gif',
    helpPath: '/help/ride-clean-up'
  },
  plannerMobile: {
    assetType: 'webm',
    helpPath: '/help/mobile-route-planning'
  },
  heatmap: {
    assetType: 'gif',
    helpPath: '/heatmap'
  }
}
